<!--
  ThreadFlowPlanCard -- Distributor App > Account > Payments.

  PR-4 of the distributor-payments redesign. Consumes PR-3's
  GET /api/distributor/billing/plan + GET /api/distributor/billing/estimated-invoice.

  Renders the tier label + trial badge, a description, the line-item charge
  grid (per-store / platform fee / shipping markup / per-order handling --
  formatted from the `charges` array by kind), a bold "Estimated <Month>
  invoice" line (from estimated-invoice), and a right-column payment-method
  card with an "Update card" affordance (emits `update-card`; the page opens
  EditPaymentInfoDialog) plus a "Change plan" link. Change plan opens this
  card's own dialog pointing to ThreadFlow Customer Support (Jake,
  2026-10-20): a plan change is not self-serve and has no proration.

  During a trial a plain notice says no MONTHLY charge is billed until the
  trial ends, and that the monthly charges listed apply after it (the month
  close bills no store and no flat fee for trial time: plan_pricer.
  flat_fee_owed and the classifier both clip the month at trial_ends_at).
  Per-order platform fees are not monthly and do apply during a trial.

  The plan's fee structure is also listed in words from `fee_terms` (the
  server's one renderer, shared with the trial reminder email and signup).

  Every dollar amount comes from the plan's `charges` (the endpoint prices
  each store class exactly as the estimate does); none is hardcoded here.

  Charge kinds & formatting:
    flat             amount_cents       -> $20.00  (a flat monthly charge on
                                           the subscription invoice, if any)
    per_store        amount_cents       -> $50.00  (store_class "full"; the
                                           "dormant" class is shown as "Per
                                           inactive store / month", always. No
                                           row = "No charge", 0 = "$0.00")
    per_order_pct    pct_basis_points   -> 3.95%      (395 bp)
    shipping_markup  markup_pct         -> × 1.10
    per_order_flat   amount_cents       -> + $1.75    ("Per-order handling")
-->
<template>
  <v-card class="tf-plan-card" data-testid="threadflow-plan-card">
    <v-card-title>Your ThreadFlow plan</v-card-title>
    <v-card-subtitle>
      What you pay us each month, and your next-invoice estimate.
    </v-card-subtitle>

    <v-card-text>
      <!-- Suspended: the subscription is paused until a working card is on
           file. This used to be a line on Company Profile's Payment Info
           block; money lives here now. The card dialog this opens is the
           same one "Update card" below opens. -->
      <div
        v-if="isSuspended"
        class="tf-suspended-notice"
        role="alert"
        data-testid="plan-suspended-notice"
      >
        <span>
          Your subscription is suspended. Contact ThreadFlow support to restore
          it. You can still update the card on file below.
        </span>
        <a
          class="tf-pm-update"
          data-testid="plan-suspended-update-card"
          role="button"
          tabindex="0"
          @click="$emit('update-card')"
          @keydown.enter="$emit('update-card')"
        >
          Update card
        </a>
      </div>

      <!-- No card on file and the grace is running: after this date the
           stores stop selling until a card is added. The API only sends the
           date while that is true (and while ThreadFlow enforces it). -->
      <div
        v-if="graceDeadlineDisplay"
        class="tf-suspended-notice"
        role="alert"
        data-testid="plan-grace-notice"
      >
        <span>
          Add a card by {{ graceDeadlineDisplay }} to keep your stores selling.
        </span>
        <a
          class="tf-pm-update"
          data-testid="plan-grace-add-card"
          role="button"
          tabindex="0"
          @click="$emit('update-card')"
          @keydown.enter="$emit('update-card')"
        >
          Add a card
        </a>
      </div>

      <div class="tf-plan-grid">
        <!-- Left column: plan + charges -->
        <div class="tf-plan-main">
          <div class="tf-plan-eyebrow-row">
            <span class="tf-plan-eyebrow">{{ tierName }}</span>
            <v-chip
              v-if="showTrialBadge"
              color="warning"
              size="small"
              label
              data-testid="plan-trial-badge"
              class="tf-plan-trial"
            >
              {{ trialBadgeText }}
            </v-chip>
          </div>

          <div class="tf-plan-name" data-testid="plan-tier-name">
            {{ planName }}
          </div>

          <div
            v-if="priceLine"
            class="tf-plan-price-line"
            data-testid="plan-price-line"
          >
            {{ priceLine }}
          </div>

          <p
            v-if="trialEndsDisplay"
            class="tf-plan-trial-notice"
            data-testid="plan-trial-notice"
          >
            No monthly charges are billed during your free trial, which ends
            on {{ trialEndsDisplay }}. After the trial, the monthly charges
            below apply.
          </p>

          <p class="tf-plan-desc" data-testid="plan-description">
            {{ description }} Platform fees come out of orders automatically.
          </p>

          <!-- Fees the plan defines but ThreadFlow is not billing yet (a flat
               monthly fee, included stores): say so, so the charges below are
               not read as the whole plan. -->
          <p
            v-if="plan.unbilled_notice"
            class="tf-plan-unbilled"
            role="note"
            data-testid="plan-unbilled-notice"
          >
            {{ plan.unbilled_notice }}
          </p>

          <PlanFeeTerms :fee-terms="plan.fee_terms" testid="plan-fee-terms" />

          <div class="tf-charge-grid">
            <template v-for="(c, i) in flatCharges" :key="`flat-${i}`">
              <span class="tf-charge-label">{{ flatLabel(c) }} / month</span>
              <span class="tf-charge-value" data-testid="plan-charge-flat">
                {{ formatCents(c.amount_cents) }}
              </span>
            </template>

            <span class="tf-charge-label">Per active store / month</span>
            <span class="tf-charge-value" data-testid="plan-charge-per-store">
              {{ perStoreDisplay }}
            </span>

            <span
              class="tf-charge-label"
              data-testid="plan-charge-per-dormant-store-label"
            >Per inactive store / month</span>
            <span
              class="tf-charge-value"
              data-testid="plan-charge-per-dormant-store"
            >
              {{ inactiveStoreDisplay }}
            </span>
            <span
              class="tf-charge-help"
              data-testid="plan-charge-per-dormant-store-help"
            >
              A store that is not live this month (a draft, inactive or closed store). Archived stores are not charged.
            </span>

            <span
              class="tf-charge-label"
              data-testid="plan-charge-platform-fee-label"
            >{{ platformFeeLabel }}</span>
            <span class="tf-charge-value" data-testid="plan-charge-platform-fee">
              {{ platformFeeDisplay }}
            </span>

            <span class="tf-charge-label">Shipping markup (carrier rate)</span>
            <span class="tf-charge-value" data-testid="plan-charge-shipping">
              {{ shippingMarkupDisplay }}
            </span>

            <span class="tf-charge-label">Per-order handling</span>
            <span class="tf-charge-value" data-testid="plan-charge-handling">
              {{ handlingDisplay }}
            </span>

            <span
              class="tf-charge-label tf-charge-total"
              data-testid="plan-estimated-invoice-month"
            >
              Estimated {{ estimatedMonth }} invoice (month to date)
            </span>
            <span
              class="tf-charge-value tf-charge-total"
              data-testid="plan-estimated-invoice"
            >
              {{ estimatedInvoiceDisplay }}
            </span>

            <div
              v-if="breakdownLines.length"
              class="tf-breakdown"
              data-testid="plan-estimate-breakdown"
            >
              <div
                v-for="(line, i) in breakdownLines"
                :key="`line-${i}`"
                class="tf-breakdown-line"
                data-testid="plan-estimate-line"
              >
                <span>{{ line.label }}</span>
                <span data-testid="plan-estimate-line-amount">{{
                  formatCents(line.amount_cents)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: payment method -->
        <div class="tf-pm-card" data-testid="plan-payment-method">
          <div class="tf-pm-eyebrow">Payment method</div>

          <template v-if="paymentMethod">
            <div class="tf-pm-line">
              <v-icon size="18" class="tf-pm-icon">credit_card</v-icon>
              {{ paymentMethod.brand }} ····{{ paymentMethod.last4 }}
            </div>
            <div class="tf-pm-exp">Exp {{ expDisplay }}</div>
          </template>
          <template v-else>
            <div class="tf-pm-line tf-pm-line--muted">
              <v-icon size="18" class="tf-pm-icon">credit_card</v-icon>
              No card on file
            </div>
          </template>

          <a
            class="tf-pm-update"
            data-testid="plan-update-card"
            role="button"
            tabindex="0"
            @click="$emit('update-card')"
            @keydown.enter="$emit('update-card')"
          >
            {{ paymentMethod ? "Update card" : "Add a card" }}
          </a>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="tf-plan-footer">
      <a
        class="tf-plan-change"
        data-testid="plan-change-link"
        role="button"
        tabindex="0"
        @click="openChangePlan"
        @keydown.enter="openChangePlan"
      >
        Change plan
      </a>
    </v-card-actions>

    <!-- Change plan: not self-serve. Vuetify's dialog traps focus and
         closes on Escape. -->
    <v-dialog
      v-model="changePlanOpen"
      max-width="460"
      aria-labelledby="plan-change-dialog-title"
    >
      <v-card data-testid="plan-change-dialog">
        <v-card-title id="plan-change-dialog-title">Change plan</v-card-title>
        <v-card-text>
          To change your plan, please contact ThreadFlow Customer Support.
          <div class="tf-plan-change-email">
            <a
              :href="`mailto:${supportEmail}`"
              data-testid="plan-change-support-email"
            >{{ supportEmail }}</a>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="flat"
            color="primary"
            data-testid="plan-change-dialog-close"
            @click="changePlanOpen = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import PlanFeeTerms from "./PlanFeeTerms.vue";
import {
  formatCents as formatPlanCents,
  planPriceLine,
} from "@/utils/planPrice";

// The address ThreadFlow support answers (also the signup and legal pages').
const SUPPORT_EMAIL = "support@threadflow.com";

export default {
  name: "ThreadFlowPlanCard",

  components: { PlanFeeTerms },

  props: {
    plan: {
      type: Object,
      required: true,
    },
    invoice: {
      type: Object,
      default: null,
    },
  },

  emits: ["update-card"],

  data() {
    return { changePlanOpen: false, supportEmail: SUPPORT_EMAIL };
  },

  computed: {
    tierName() {
      return this.plan?.tier?.name || "ThreadFlow plan";
    },

    // The subscribed plan by its own name ("Test Plan"); a tier groups
    // several plans, so the tier name is only the fallback.
    planName() {
      return this.plan?.plan?.name || this.tierName;
    },

    subscriptionState() {
      return this.plan?.subscription?.state || null;
    },

    isSuspended() {
      return this.subscriptionState === "suspended";
    },

    trialDaysLeft() {
      return this.plan?.subscription?.trial_days_left ?? null;
    },

    // The API rounds the remaining days UP and flags the last 24 hours.
    trialBadgeText() {
      if (this.plan?.subscription?.trial_ends_today) {
        return "Trial ends today";
      }
      const n = this.trialDaysLeft;
      return `Trial · ${n} ${n === 1 ? "day" : "days"} left`;
    },

    // Hidden when active OR when there is no trial countdown.
    showTrialBadge() {
      return (
        this.subscriptionState !== "active" &&
        this.trialDaysLeft != null &&
        this.trialDaysLeft > 0
      );
    },

    paymentMethod() {
      return this.plan?.payment_method || null;
    },

    // "November 18" (reader's time zone) while a trial is running; "" hides
    // the trial notice. Same visibility rule as the trial badge.
    trialEndsDisplay() {
      if (!this.showTrialBadge) return "";
      const iso = this.plan?.subscription?.trial_ends_at;
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    },

    // "October 22", in the reader's own time zone; "" hides the notice.
    graceDeadlineDisplay() {
      const iso = this.plan?.subscription?.no_card_grace_ends_at;
      if (!iso || this.paymentMethod) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    },

    expDisplay() {
      const pm = this.paymentMethod;
      if (!pm || pm.exp_month == null || pm.exp_year == null) return "";
      const mm = String(pm.exp_month).padStart(2, "0");
      return `${mm}/${pm.exp_year}`;
    },

    charges() {
      return Array.isArray(this.plan?.charges) ? this.plan.charges : [];
    },

    // Flat monthly charges on the subscription invoice (the estimate adds
    // exactly these). A flat charge billed another way is not a monthly line.
    flatCharges() {
      return this.charges.filter(
        (x) =>
          x &&
          x.kind === "flat" &&
          (x.applies_at == null || x.applies_at === "subscription_invoice")
      );
    },

    // The plan's price in one line ("$9.99/mo + $99.00/active store"), from
    // the shared helper every plan-price surface uses. Built from the same
    // rows the grid lists, so it never states a fee the grid (and the
    // estimate) does not: a flat fee only when listed, a per-store price
    // only when it could be read.
    priceLine() {
      const flats = this.flatCharges.filter((c) => c.amount_cents != null);
      const full = this.charges.find(
        (x) => x && x.kind === "per_store" && x.store_class !== "dormant"
      );
      const unbilled = this.plan?.unbilled_terms || null;
      const billedMonthly = flats.length
        ? flats.reduce((sum, c) => sum + c.amount_cents, 0)
        : null;
      const line = planPriceLine({
        // The plan's monthly fee is stated even while it is not billed yet
        // (#476); a billed flat fee wins when both exist.
        monthlyCents: billedMonthly ?? unbilled?.monthly_fee_cents ?? null,
        perActiveCents: full ? full.amount_cents ?? null : null,
      });
      if (!line || !unbilled) return line;
      const n = unbilled.included_stores;
      const included = n
        ? `, ${n} ${n === 1 ? "store" : "stores"} included`
        : "";
      return `${line}${included} (not billed yet)`;
    },

    description() {
      return this.flatCharges.length
        ? "A flat monthly charge, plus a monthly charge for each store based on how the store was used that month."
        : "A monthly charge for each store, based on how the store was used that month.";
    },

    // The endpoint prices a store class exactly as the estimate does (the
    // pinned version, else the plan's live version, overrides applied), so
    // the card never shows a dash for a class the estimate bills. Pick by
    // class, never by position; a row with no class predates the split and
    // means the per-active-store price.
    perStoreDisplay() {
      return this.storeClassDisplay(
        this.charges.find(
          (x) => x && x.kind === "per_store" && x.store_class !== "dormant"
        )
      );
    },

    // The DORMANT class, shown to the distributor as an "inactive" store.
    inactiveStoreDisplay() {
      return this.storeClassDisplay(
        this.charges.find(
          (x) => x && x.kind === "per_store" && x.store_class === "dormant"
        )
      );
    },

    // One line per component of the estimate, so the shown rows explain the
    // total (they are the estimate's own lines, and sum to it).
    breakdownLines() {
      const items = this.invoice?.line_items;
      return Array.isArray(items) ? items : [];
    },

    platformFeeDisplay() {
      const c = this.chargeByKind("per_order_pct");
      return c ? this.formatBasisPoints(c.pct_basis_points) : "—";
    },

    // What the fee applies to, with the rate from the plan data (never a
    // hard-coded percentage). Without a readable rate the row is just the
    // plain name and its dash.
    platformFeeLabel() {
      const c = this.chargeByKind("per_order_pct");
      if (!c || c.pct_basis_points == null) return "Platform fee";
      return `Platform fee: ${this.formatBasisPoints(c.pct_basis_points)} of the selling price of apparel and decoration (not shipping or tax)`;
    },

    shippingMarkupDisplay() {
      const c = this.chargeByKind("shipping_markup");
      return c ? this.formatMarkup(c.markup_pct) : "—";
    },

    handlingDisplay() {
      const c = this.chargeByKind("per_order_flat");
      return c ? `+ ${this.formatCents(c.amount_cents)}` : "—";
    },

    estimatedInvoiceDisplay() {
      if (!this.invoice || this.invoice.estimated_total_cents == null) {
        return "—";
      }
      return this.formatCents(this.invoice.estimated_total_cents);
    },

    estimatedMonth() {
      // period_month ("YYYY-MM") is the month the next month close bills,
      // read from the month-close calendar in the distributor's timezone.
      // Never derive it from period_end: that is the close instant, which
      // falls in the FOLLOWING month.
      const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(
        this.invoice?.period_month || ""
      );
      if (!match) return "next";
      // A fixed mid-month UTC date: the month name cannot roll with the
      // browser's time zone.
      const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 15));
      return d.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
    },
  },

  methods: {
    openChangePlan() {
      this.changePlanOpen = true;
    },

    chargeByKind(kind) {
      return this.charges.find((c) => c && c.kind === kind) || null;
    },

    flatLabel(charge) {
      return charge.label || "Subscription fee";
    },

    // No row = the plan has no charge for this class ("No charge"); a row
    // with amount 0 is a real $0.00; a row with no amount is a price that
    // exists but could not be resolved, which is neither free nor a dash.
    storeClassDisplay(charge) {
      if (!charge) return "No charge";
      if (charge.amount_cents == null) return "Not available";
      const notes = [];
      if (charge.override === "suppress") notes.push("comped");
      else if (charge.override === "custom_rate") notes.push("custom rate");
      if (charge.price_source === "live_fallback") {
        notes.push("current plan price");
      }
      const amount = this.formatCents(charge.amount_cents);
      return notes.length ? `${amount} (${notes.join(", ")})` : amount;
    },

    // The shared cents formatter; a missing amount is the card's dash.
    formatCents(cents) {
      if (cents == null) return "—";
      return formatPlanCents(cents);
    },

    // 395 basis points -> "3.95%"
    formatBasisPoints(bp) {
      if (bp == null) return "—";
      return `${(bp / 100).toFixed(2)}%`;
    },

    // markup_pct is a PERCENT (PR-3 contract): 10 means a 10% markup, i.e.
    // the carrier rate × 1.10. Render the multiplier, not the raw percent.
    // 10 -> "× 1.10"
    formatMarkup(pct) {
      if (pct == null) return "—";
      return `× ${(1 + Number(pct) / 100).toFixed(2)}`;
    },
  },
};
</script>

<style scoped>
.tf-plan-card {
  padding: var(--tf-sp-2, 8px) var(--tf-sp-2, 8px) var(--tf-sp-2, 8px);
}
.tf-suspended-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid var(--tf-warning, #b7791f);
  border-radius: 6px;
  background: var(--tf-warning-soft, #fffaf0);
  color: var(--tf-fg-1, #1a2733);
  font-size: 13px;
}
.tf-suspended-notice .tf-pm-update {
  margin-top: 0;
  white-space: nowrap;
}
.tf-plan-grid {
  display: grid;
  grid-template-columns: 1fr 240px;
  gap: 24px;
  align-items: flex-start;
}
.tf-plan-eyebrow-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.tf-plan-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: var(--tf-fg-4, #8a97a3);
  letter-spacing: 1.4px;
  text-transform: uppercase;
}
.tf-plan-trial {
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.tf-plan-name {
  font-size: 20px;
  font-weight: 500;
  color: var(--tf-fg-1, #1a2733);
  margin-bottom: 10px;
}
.tf-plan-price-line {
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-2, #34424d);
  margin: -4px 0 10px;
}
.tf-plan-trial-notice {
  font-size: 13px;
  color: var(--tf-fg-2, #34424d);
  line-height: 1.55;
  margin-bottom: 10px;
}
.tf-plan-unbilled {
  font-size: 13px;
  color: var(--tf-fg-1, #1a2733);
  line-height: 1.55;
  margin-bottom: 10px;
  padding: 8px 12px;
  border: 1px solid var(--tf-warning, #b7791f);
  border-radius: 6px;
  background: var(--tf-warning-soft, #fffaf0);
}
.tf-plan-change-email {
  margin-top: 8px;
}
.tf-plan-desc {
  font-size: 13px;
  color: var(--tf-fg-3, #56636e);
  line-height: 1.55;
  margin-bottom: 14px;
}
.tf-charge-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 6px 14px;
  font-size: 13px;
  color: var(--tf-fg-2, #34424d);
}
.tf-charge-value {
  color: var(--tf-fg-4, #8a97a3);
  text-align: right;
}
.tf-charge-help {
  grid-column: 1 / -1;
  font-size: 12px;
  color: var(--tf-fg-4, #8a97a3);
  margin-top: -4px;
}
.tf-breakdown {
  grid-column: 1 / -1;
  display: grid;
  gap: 2px;
  font-size: 12px;
  color: var(--tf-fg-3, #56636e);
}
.tf-breakdown-line {
  display: flex;
  justify-content: space-between;
  gap: 14px;
}
.tf-charge-total {
  font-weight: 500;
  color: var(--tf-fg-1, #1a2733);
  padding-top: 8px;
  border-top: 1px solid var(--tf-border, #e3e8ec);
}
.tf-pm-card {
  background: var(--tf-bg-soft, #fafbfc);
  border: 1px solid var(--tf-border, #e3e8ec);
  border-radius: 10px;
  padding: 14px 16px;
}
.tf-pm-eyebrow {
  font-size: 11px;
  color: var(--tf-fg-4, #8a97a3);
  font-weight: 500;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.tf-pm-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--tf-fg-1, #1a2733);
  font-weight: 500;
}
.tf-pm-line--muted {
  color: var(--tf-fg-4, #8a97a3);
  font-weight: 400;
}
.tf-pm-icon {
  color: var(--tf-fg-4, #8a97a3);
}
.tf-pm-exp {
  font-size: 11px;
  color: var(--tf-fg-4, #8a97a3);
  margin-top: 4px;
}
.tf-pm-update {
  color: var(--tf-primary, #2b6cb0);
  font-size: 12px;
  cursor: pointer;
  margin-top: 10px;
  display: inline-block;
}
.tf-plan-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  font-size: 13px;
  color: var(--tf-fg-3, #56636e);
  padding: 8px 16px 16px;
}
.tf-plan-change {
  color: var(--tf-primary, #2b6cb0);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
}
</style>
